import {
  ReasonForNegativeScore,
  QaScore,
} from "@/v2/entities/qa-score/QaScore.model";
import { questionToReasons } from "@/v2/entities/qa-score/questionToReasons";
import { QaScoreResultLabel } from "./QaScoreResultLabel";
import { QaScoreResultNumeric } from "./QaScoreResultNumeric";
import { QaScoreResultBinary } from "./QaScoreResultBinary";
import { QaScoreComment } from "./QaScoreComment";
import { QaScoreReasons } from "./QaScoreReasons";
import styles from "./QaScore.module.scss";
import { useCallback } from "react";

type UpdateTicketScore = {
  adjustedScore?: number;
  comment?: string;
  reasonsForNegativeScore?: ReasonForNegativeScore[];
};

interface QaScoreResultProps extends QaScore {
  updateQaScore: (newScore: UpdateTicketScore) => Promise<void>;
  viewOnly?: boolean;
  showReasons?: boolean;
  showComment?: boolean;
}

export function QaScoreResult({
  type,
  id,
  content,
  autoScore,
  adjustedScore,
  reasonsForNegativeScore,
  comment,
  brief,
  updateQaScore,
  viewOnly = false,
  showReasons = false,
  showComment = false,
}: QaScoreResultProps) {
  const value = adjustedScore ?? autoScore;

  const updateScore = useCallback(
    (adjustedScore: number) => updateQaScore({ adjustedScore }),
    [updateQaScore]
  );

  const updateScoreReasons = useCallback(
    (reasonsForNegativeScore: ReasonForNegativeScore[]) =>
      updateQaScore({ reasonsForNegativeScore }),
    [updateQaScore]
  );

  const updateScoreComment = useCallback(
    (comment: string) => updateQaScore({ comment }),
    [updateQaScore]
  );

  if (type === "binary") {
    return (
      <div className={styles.QaScore}>
        <QaScoreResultLabel
          type={type}
          autoScore={autoScore}
          adjustedScore={adjustedScore}
        />

        <QaScoreResultBinary
          value={value}
          updateScore={updateScore}
          viewOnly={viewOnly}
        />

        {showReasons && (
          <>
            <div className={styles.Label}>Reasons</div>

            <QaScoreReasons
              reasons={questionToReasons[content]}
              value={reasonsForNegativeScore}
              updateScoreReasons={updateScoreReasons}
              viewOnly={true}
            />
          </>
        )}

        {showComment && (
          <>
            <div className={styles.Label}>Comment</div>

            <QaScoreComment
              value={comment}
              updateScoreComment={updateScoreComment}
              viewOnly={true}
            />
          </>
        )}

        {brief && (
          <>
            <div className={styles.Label}>Brief</div>
            <div className={styles.QaScoreBrief}>{brief}</div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={styles.QaScore}>
      <QaScoreResultLabel
        type={type}
        autoScore={autoScore}
        adjustedScore={adjustedScore}
      />

      <QaScoreResultNumeric
        value={value}
        updateScore={updateScore}
        viewOnly={viewOnly}
      />

      {showComment && (
        <>
          <div className={styles.Label}>Comment</div>

          <QaScoreComment
            value={comment}
            updateScoreComment={updateScoreComment}
            viewOnly={true}
          />
        </>
      )}

      {brief && (
        <>
          <div className={styles.Label}>Brief</div>
          <div className={styles.QaScoreBrief}>{brief}</div>
        </>
      )}
    </div>
  );
}
